/* predictive-search.js — live suggestions for the always-on header bar and the
   mobile search drawer. Adds: rotating "typewriter" placeholder, small-typo
   tolerance (retries a trimmed query when nothing matches), and site-sale
   discounted pricing in the results (mirrors the product cards). */
(function () {
  'use strict';
  var theme = window.theme || {};
  // Search suggestions show the variant's own price and compare-at, nothing
  // computed. This used to derive a discount from theme.sale when the sale was
  // store-wide — the predictive-search JSON carries no collection data, so a
  // scoped sale had to be skipped here anyway. Since 2026-08-14 the sale is real
  // compare-at pricing, so the JSON already contains both numbers and there is
  // nothing to infer.

  // Americana prompts for the typewriter placeholder.
  var TERMS = ['Civil War', 'George Washington', 'Cowboys', 'Bald eagle', 'American flag',
    'Abraham Lincoln', 'The Revolution', 'Route 66', 'Statue of Liberty', 'Gettysburg'];

  function esc(s) { var d = document.createElement('div'); d.textContent = s == null ? '' : s; return d.innerHTML; }

  function money(amount) {
    var fmt = theme.moneyFormat || '${{amount}}';
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, esc(amount));
  }

  // Apply the store-wide sale to a formatted price string, preserving decimals.
  function priceHtml(p) {
    if (typeof p.price !== 'string' || !p.price) return '';
    var compare = p.compare_at_price || p.compare_at_price_max;
    var hasCompare = compare && parseFloat(String(compare).replace(/[^0-9.]/g, '')) > parseFloat(String(p.price).replace(/[^0-9.]/g, ''));
    if (hasCompare) {
      return '<span class="ps-item__price ps-item__price--sale"><span class="ps-item__now">' + money(p.price) +
        '</span> <s class="ps-item__was">' + money(compare) + '</s></span>';
    }
    return '<span class="ps-item__price">' + money(p.price) + '</span>';
  }

  function render(container, q, products) {
    var html = '';
    if (products.length) {
      html += '<ul class="ps-list" role="list">';
      products.forEach(function (p) {
        var src = p.image || (p.featured_image && p.featured_image.url) || '';
        var media = src ? '<img src="' + src + '" alt="" loading="lazy">' : '<span class="ps-item__ph"></span>';
        html += '<li><a class="ps-item" href="' + p.url + '"><span class="ps-item__media">' + media + '</span>' +
          '<span class="ps-item__title">' + esc(p.title) + '</span>' + priceHtml(p) + '</a></li>';
      });
      html += '</ul>';
    } else {
      html += '<p class="ps-empty">' + ((theme.strings && theme.strings.noResults) || 'No matches yet') + '</p>';
    }
    // Link to the same scoped result set the form would submit, but keep the
    // shopper's own words in the label.
    var sc = theme.searchScope;
    var linkQ = (sc && !sc.isAlreadyScoped(q) && sc.build(q, false)) || q;
    html += '<a class="ps-all" href="' + ((theme.routes && theme.routes.search) || '/search') + '?q=' +
      encodeURIComponent(linkQ) + '&type=product">' + ((theme.strings && theme.strings.searchFor) || 'Search for') + ' “' + esc(q) + '” →</a>';
    container.innerHTML = html;
    container.hidden = false;
  }

  function suggestUrl(q) {
    return (theme.routes && theme.routes.predictiveSearch ? theme.routes.predictiveSearch : '/search/suggest') +
      '.json?q=' + encodeURIComponent(q) +
      '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last';
  }

  function fetchProducts(q) {
    return fetch(suggestUrl(q))
      .then(function (r) { return r.json(); })
      .then(function (data) {
        return (data.resources && data.resources.results && data.resources.results.products) || [];
      });
  }

  /* Suggestions are scoped to title/tag/product_type just like the results page,
     so the dropdown can't surface prints that merely mention the term in their
     description. Three passes, widening only when the tighter one comes up dry:
       1. exact   — (title:Lincoln OR tag:Lincoln OR product_type:Lincoln)
       2. prefix  — the same with * appended, so "linco" still finds Lincoln
       3. raw     — Shopify's default search, rather than showing nothing
     Passes 2 and 3 subsume the old trimmed-prefix typo tolerance. */
  function fetchScoped(q) {
    var sc = theme.searchScope;
    if (!sc || sc.isAlreadyScoped(q)) return fetchProducts(q);

    var exact = sc.build(q, false);
    if (!exact) return fetchProducts(q);

    return fetchProducts(exact).then(function (products) {
      if (products.length) return products;
      return fetchProducts(sc.build(q, true)).then(function (p2) {
        return p2.length ? p2 : fetchProducts(q);
      });
    });
  }

  function bind(input) {
    var scope = input.closest('[data-predictive-scope]') || document;
    var results = scope.querySelector('[data-predictive-results]');
    if (!results) return;
    var timer;

    function search() {
      var q = input.value.trim();
      if (q.length < 2) { results.innerHTML = ''; results.hidden = true; return; }
      fetchScoped(q).then(function (products) {
        render(results, q, products);
      }).catch(function () {});
    }

    input.addEventListener('input', function () { clearTimeout(timer); timer = setTimeout(search, 220); });
    input.addEventListener('focus', function () { if (input.value.trim().length >= 2 && results.innerHTML) results.hidden = false; });

    // Close the dropdown on outside click / Escape
    document.addEventListener('click', function (e) {
      if (!scope.contains(e.target)) results.hidden = true;
    });
    input.addEventListener('keydown', function (e) { if (e.key === 'Escape') results.hidden = true; });
  }

  /* ---- rotating "typewriter" placeholder ---- */
  function typewriter(input) {
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { input.placeholder = 'Search “' + TERMS[0] + '”'; return; }
    var ti = 0, ci = 0, deleting = false;
    function tick() {
      // Pause while the field is in use.
      if (document.activeElement === input || input.value) { return setTimeout(tick, 1200); }
      var term = TERMS[ti];
      if (!deleting) {
        ci++;
        input.placeholder = 'Search “' + term.slice(0, ci) + '”';
        if (ci >= term.length) { deleting = true; return setTimeout(tick, 1500); }
        return setTimeout(tick, 85);
      }
      ci--;
      input.placeholder = 'Search “' + term.slice(0, Math.max(0, ci)) + '”';
      if (ci <= 0) { deleting = false; ti = (ti + 1) % TERMS.length; return setTimeout(tick, 300); }
      return setTimeout(tick, 40);
    }
    tick();
  }

  function init() {
    document.querySelectorAll('[data-predictive-input]').forEach(function (input) {
      bind(input);
      if (input.hasAttribute('data-typewriter')) typewriter(input);
    });

    // Header search icon (only present when the always-on bar is disabled) → open drawer
    var drawer = document.getElementById('search-drawer');
    document.addEventListener('click', function (e) {
      var toggle = e.target.closest('[data-search-toggle]');
      if (toggle && drawer && theme.drawers) {
        e.preventDefault();
        theme.drawers.open('search-drawer');
        var di = drawer.querySelector('[data-predictive-input]');
        if (di) setTimeout(function () { di.focus(); }, 90);
      }
    });
  }

  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', init); }
  else { init(); }
})();
